// hooks/useScrollSpy.ts
import { useEffect } from 'react';

type Section = { id: string; anchor: string };

export function useScrollSpy(
  sections: Section[],
  onActiveChange: (id: string) => void,
  options?: { topOffset?: number; bottomOffsetRatio?: number }
) {
  const topOffset = options?.topOffset ?? 100;
  const bottomOffsetRatio = options?.bottomOffsetRatio ?? 0.6;

  useEffect(() => {
    const getCurrentSectionId = () => {
      if (sections.length === 0) return null;

      const lastSection = sections[sections.length - 1];
      const lastElement = document.getElementById(lastSection.anchor);

      if (lastElement) {
        const bottomCheckpoint =
          globalThis.scrollY + Math.floor(globalThis.innerHeight * bottomOffsetRatio);
        if (bottomCheckpoint >= lastElement.offsetTop) {
          return lastSection.id;
        }
      }

      const checkpoint = globalThis.scrollY + topOffset + 12;
      let currentId: string | null = null;

      for (const section of sections) {
        const element = document.getElementById(section.anchor);
        if (!element) continue;

        if (element.offsetTop <= checkpoint) {
          currentId = section.id;
        } else {
          break;
        }
      }

      return currentId ?? sections[0].id;
    };

    let rafId: number | null = null;

    const updateActiveSection = () => {
      if (rafId !== null) return;

      rafId = globalThis.requestAnimationFrame(() => {
        rafId = null;
        const nextId = getCurrentSectionId();
        if (nextId) {
          onActiveChange(nextId);
        }
      });
    };

    updateActiveSection();
    globalThis.addEventListener('scroll', updateActiveSection, { passive: true });
    globalThis.addEventListener('resize', updateActiveSection);

    return () => {
      if (rafId !== null) {
        globalThis.cancelAnimationFrame(rafId);
      }
      globalThis.removeEventListener('scroll', updateActiveSection);
      globalThis.removeEventListener('resize', updateActiveSection);
    };
  }, [sections, onActiveChange, topOffset, bottomOffsetRatio]);
}
